import { supabase } from './supabase';

export interface RssFeedSource {
  id: string;
  name: string;
  url: string;
  category: string;
  description?: string;
  logo_url?: string;
  is_active: boolean;
  last_fetched_at?: string;
  fetch_frequency_minutes: number;
  created_at?: string;
  updated_at?: string;
}

export interface RssFeedItem {
  id: string;
  source_id: string;
  title: string;
  link: string;
  description?: string;
  content?: string;
  author?: string;
  categories?: string[];
  published_at: string;
  guid?: string;
  image_url?: string;
  is_read: boolean;
  is_saved: boolean;
  created_at?: string;
  source?: {
    name: string;
    category: string;
  };
}

export interface RssFeedUserPreferences {
  id: string;
  user_id: string;
  source_ids?: string[];
  categories?: string[];
  keywords?: string[];
  excluded_keywords?: string[];
  refresh_frequency_minutes: number;
  max_items_per_source: number;
  created_at?: string;
  updated_at?: string;
}

export interface FetchFeedsOptions {
  sourceId?: string;
  category?: string;
  limit?: number;
  refresh?: boolean;
}

/**
 * Fetches RSS feed items from the server
 */
export async function fetchRssFeeds(options: FetchFeedsOptions = {}): Promise<RssFeedItem[]> {
  try {
    const { sourceId, category, limit = 20, refresh = false } = options;
    
    // Build query parameters
    const params = new URLSearchParams();
    if (sourceId) params.append('sourceId', sourceId);
    if (category) params.append('category', category);
    if (limit) params.append('limit', limit.toString());
    if (refresh) params.append('refresh', 'true');
    
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-rss-feeds?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch RSS feeds: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.items;
  } catch (error) {
    console.error('Error fetching RSS feeds:', error);
    throw error;
  }
}

/**
 * Fetches all RSS feed sources
 */
export async function fetchRssFeedSources(): Promise<RssFeedSource[]> {
  try {
    const { data, error } = await supabase
      .from('rss_feed_sources')
      .select('*')
      .eq('is_active', true)
      .order('name');
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching RSS feed sources:', error);
    throw error;
  }
}

/**
 * Adds a new RSS feed source
 */
export async function addRssFeedSource(source: Omit<RssFeedSource, 'id' | 'created_at' | 'updated_at'>): Promise<RssFeedSource> {
  try {
    const { data, error } = await supabase
      .from('rss_feed_sources')
      .insert(source)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding RSS feed source:', error);
    throw error;
  }
}

/**
 * Updates an existing RSS feed source
 */
export async function updateRssFeedSource(id: string, updates: Partial<RssFeedSource>): Promise<RssFeedSource> {
  try {
    const { data, error } = await supabase
      .from('rss_feed_sources')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating RSS feed source:', error);
    throw error;
  }
}

/**
 * Deletes an RSS feed source
 */
export async function deleteRssFeedSource(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('rss_feed_sources')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting RSS feed source:', error);
    throw error;
  }
}

/**
 * Marks an RSS feed item as read or unread
 */
export async function markRssItemRead(itemId: string, isRead: boolean = true): Promise<RssFeedItem> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mark-rss-read`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ itemId, isRead }),
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to mark item as ${isRead ? 'read' : 'unread'}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error(`Error marking item as ${isRead ? 'read' : 'unread'}:`, error);
    throw error;
  }
}

/**
 * Saves or unsaves an RSS feed item
 */
export async function saveRssItem(itemId: string, isSaved: boolean = true): Promise<RssFeedItem> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save-rss-item`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ itemId, isSaved }),
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to ${isSaved ? 'save' : 'unsave'} item: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error(`Error ${isSaved ? 'saving' : 'unsaving'} item:`, error);
    throw error;
  }
}

/**
 * Gets user preferences for RSS feeds
 */
export async function getUserRssPreferences(): Promise<RssFeedUserPreferences | null> {
  try {
    const { data, error } = await supabase
      .from('rss_feed_user_preferences')
      .select('*')
      .single();
    
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "no rows returned"
    return data;
  } catch (error) {
    console.error('Error fetching user RSS preferences:', error);
    throw error;
  }
}

/**
 * Updates user preferences for RSS feeds
 */
export async function updateUserRssPreferences(preferences: Partial<RssFeedUserPreferences>): Promise<RssFeedUserPreferences> {
  try {
    // First check if preferences exist
    const { data: existingPrefs } = await supabase
      .from('rss_feed_user_preferences')
      .select('id')
      .single();
    
    let data;
    let error;
    
    if (existingPrefs) {
      // Update existing preferences
      ({ data, error } = await supabase
        .from('rss_feed_user_preferences')
        .update(preferences)
        .eq('id', existingPrefs.id)
        .select()
        .single());
    } else {
      // Create new preferences
      const { data: user } = await supabase.auth.getUser();
      
      if (!user.user) {
        throw new Error('User not authenticated');
      }
      
      ({ data, error } = await supabase
        .from('rss_feed_user_preferences')
        .insert({
          user_id: user.user.id,
          ...preferences
        })
        .select()
        .single());
    }
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user RSS preferences:', error);
    throw error;
  }
}

/**
 * Gets saved RSS feed items
 */
export async function getSavedRssItems(): Promise<RssFeedItem[]> {
  try {
    const { data, error } = await supabase
      .from('rss_feed_items')
      .select(`
        *,
        source:source_id(name, category)
      `)
      .eq('is_saved', true)
      .order('published_at', { ascending: false });
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching saved RSS items:', error);
    throw error;
  }
}